import { useMemo } from 'react';

import type { BannerDefaultProps } from '@rtkwlf/fenrir-react';
import { SolveTicketInputClosureReasonEnum } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import type { TicketRuleTypeSelectValue } from '../../ticket-rule-type-select/use-ticket-rule-type-select-options/use-ticket-rule-type-select-options';
import {
  type ActionType,
  CloseAction,
  CommentAction,
} from '../../ticketAction.constants';

export type ActionTypeFormConfig = {
  closureReason?: SolveTicketInputClosureReasonEnum;
  bannerProps?: BannerDefaultProps;
  shouldComment?: boolean;
  canAttachFile?: boolean;
  hasCancelButton?: boolean;
  submitButtonText: string;
};

export function useTicketActionConfig({
  actionType,
  ruleType,
}: {
  actionType: ActionType;
  ruleType?: TicketRuleTypeSelectValue | '';
}): ActionTypeFormConfig {
  const { t } = useTranslation('ticket');

  return useMemo(() => {
    const config: Record<ActionType, ActionTypeFormConfig> = {
      [CommentAction.Comment]: {
        shouldComment: true,
        canAttachFile: true,
        hasCancelButton: true,
        submitButtonText: t('addCommentButton'),
      },
      [CloseAction.Close]: {
        bannerProps: {
          appearance: 'warning',
          title: t(`actionMessage.${CloseAction.Close}.title`),
          description: t(`actionMessage.${CloseAction.Close}.description`),
          marginTop: 'small',
        },
        closureReason: SolveTicketInputClosureReasonEnum.CloseRequest,
        submitButtonText: t('action.closeTicket'),
      },
      [CloseAction.Request]: {
        closureReason: SolveTicketInputClosureReasonEnum.FollowUpRequest,
        shouldComment: true,
        canAttachFile: true,
        hasCancelButton: true,
        submitButtonText: t('action.closeTicket'),
      },
      [CloseAction.Suppress]: {
        closureReason: SolveTicketInputClosureReasonEnum.SuppressionRequest,
        shouldComment: true,
        canAttachFile: true,
        hasCancelButton: true,
        submitButtonText: t('action.closeTicket'),
      },
      [CloseAction.ManageAlertConfiguration]: {
        closureReason: SolveTicketInputClosureReasonEnum.CloseRequest,
        shouldComment: !ruleType || ruleType === 'other',
        canAttachFile: !ruleType || ruleType === 'other',
        hasCancelButton: true,
        submitButtonText: ruleType
          ? ruleType === 'other'
            ? t('action.closeAndRequest')
            : t('action.closeAndSubmit')
          : t('action.closeTicket'),
        bannerProps:
          ruleType === 'other'
            ? {
                marginTop: 'small',
                appearance: 'info',
                title: t(
                  `actionMessage.${CloseAction.ManageAlertConfiguration}.${ruleType}.title`,
                ),
                description: t(
                  `actionMessage.${CloseAction.ManageAlertConfiguration}.${ruleType}.description`,
                ),
              }
            : undefined,
      },
    };

    return config[actionType];
  }, [t, actionType, ruleType]);
}
